import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type {
    SourcesDatable,
    DamLevelsForCalendar,
    ReadingsState,
    DamMap,
    DamChartData,
    DamLevelStat,
} from "@/types";
import axios, { AxiosError, AxiosHeaderValue } from 'axios';
import {
    sourcesDatatableURL,
    damLevelsForCalendarURL,
    damsMapURL,
    inflowChartDataURL,
    damLevelStatsURL,
} from "../../constants";
import {destroyToten} from '../authentications/authenticationsSlice'


interface ErrorMessage {
  error: string;
  message: string;
}



interface getSourcesDatatableArgsType {
  token: string | null;
}

interface getDamLevelsForCalendarArgsType {
  token: string | null;
  payload:{sourceId: string | undefined| null}
}

interface getDamLevelStatsArgsType {
  token: string | null;
  payload:{name: string | undefined| null}
}



export const initialState: ReadingsState = {
  getStatus: 'idle',
  error: null,
  message: null,
  sourcesDatatable: [],
  damLevelsForCalendar: [],
  damsMap: [],
  inflowChartData: {
    labels: [],
    datasets: []
  },
  damLevelStats: {} as DamLevelStat,
}


export const getSourcesDatables = createAsyncThunk<SourcesDatable[], getSourcesDatatableArgsType, { rejectValue: ErrorMessage }>(
  "readings/getSourcesDatables",
  async (data: getSourcesDatatableArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(sourcesDatatableURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getDamsMap = createAsyncThunk<DamMap[], getSourcesDatatableArgsType, { rejectValue: ErrorMessage }>(
  "readings/getDamsMap",
  async (data: getSourcesDatatableArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(damsMapURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getInflowChartData = createAsyncThunk<DamChartData, getSourcesDatatableArgsType, { rejectValue: ErrorMessage }>(
  "readings/getInflowChartData",
  async (data: getSourcesDatatableArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(inflowChartDataURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);




export const getDamLevelsForCalendar = createAsyncThunk<DamLevelsForCalendar[], getDamLevelsForCalendarArgsType, { rejectValue: ErrorMessage }>(
  "readings/getDamLevelsForCalendar",
  async (data: getDamLevelsForCalendarArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     let params = data?.payload;
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(damLevelsForCalendarURL, {params});
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDamLevelStats = createAsyncThunk<DamLevelStat, getDamLevelStatsArgsType, { rejectValue: ErrorMessage }>(
  "readings/getDamLevelStats",
  async (data: getDamLevelStatsArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     let params = data?.payload;
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(damLevelStatsURL, {params});
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const readingsSlice = createSlice({
  name: 'readings',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSourcesDatables.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getSourcesDatables.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.sourcesDatatable = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getSourcesDatables.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.sourcesDatatable = [];
      })
      .addCase(getDamLevelsForCalendar.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDamLevelsForCalendar.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.damLevelsForCalendar = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDamLevelsForCalendar.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.damLevelsForCalendar = [];
      })
      .addCase(getDamsMap.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDamsMap.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.damsMap = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDamsMap.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.damsMap = [];
      })
      .addCase(getInflowChartData.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getInflowChartData.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.inflowChartData = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getInflowChartData.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.inflowChartData = initialState.inflowChartData;
      })
      .addCase(getDamLevelStats.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDamLevelStats.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.damLevelStats = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDamLevelStats.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.damLevelStats = initialState.damLevelStats;
      })
    

    }
})

export default readingsSlice.reducer
